import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export const roleLabels: Record<string, string> = {
  frontend: "フロントエンド",
  backend: "バックエンド",
  infra: "インフラ / SRE",
  mobile: "モバイル",
  data_ml: "データ / ML",
};

export type RoleAssessmentView = {
  score: number;
  rank: string;
  comment: string;
  evidence: string[];
};
export type RoleFitEntryView = {
  role: string;
  fitRationale: string;
  resume: RoleAssessmentView | null;
  interview: RoleAssessmentView | null;
};
export type RoleFitView = {
  primaryRole: string | null;
  primaryRationale: string | null;
  entries: RoleFitEntryView[];
};

const ASSESSMENT_SIDES = [
  ["resume", "職務経歴書"],
  ["interview", "面接"],
] as const;

const ROLE_FIT_EMPTY_MESSAGE = "職種適性は未評価です（再評価で生成されます）";
const ROLE_FIT_NO_ROLE_MESSAGE = "該当する職種はありません。";
const ASSESSMENT_MISSING_MESSAGE = "評価なし（材料なし）";

export function rankBadgeVariant(rank: string): "default" | "secondary" | "outline" {
  if (rank === "S" || rank === "A") return "default";
  if (rank === "B") return "secondary";
  return "outline";
}

function AssessmentScore({ assessment }: { assessment: RoleAssessmentView }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <Badge variant={rankBadgeVariant(assessment.rank)}>{assessment.rank}</Badge>
        <span className="text-sm text-slate-500">{assessment.score} / 100</span>
      </div>
      <Progress
        value={assessment.score}
        className="h-2"
        aria-label={`スコア ${assessment.score}`}
      />
    </div>
  );
}

function AssessmentColumn({
  label,
  assessment,
}: { label: string; assessment: RoleAssessmentView | null }) {
  return (
    <div className="space-y-2 rounded-lg bg-slate-50 p-3">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      {assessment ? (
        <>
          <AssessmentScore assessment={assessment} />
          <p className="text-sm leading-7 text-slate-700">{assessment.comment}</p>
          {assessment.evidence.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600">
                根拠 {assessment.evidence.length} 件
              </summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 leading-7 text-slate-600">
                {assessment.evidence.map((line, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: 並べ替えのない根拠一覧で重複する文を区別するため
                  <li key={index}>{line}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{ASSESSMENT_MISSING_MESSAGE}</p>
      )}
    </div>
  );
}

export function RoleFitCard({ roleFit }: { roleFit: RoleFitView | null }) {
  const primary = roleFit?.entries.find((entry) => entry.role === roleFit.primaryRole) ?? null;
  return (
    <Card data-testid="role-fit-card">
      <CardHeader>
        <CardTitle>職種適性</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {roleFit === null && (
          <p className="text-sm text-muted-foreground">{ROLE_FIT_EMPTY_MESSAGE}</p>
        )}
        {roleFit !== null && !primary && (
          <p className="text-sm text-muted-foreground">{ROLE_FIT_NO_ROLE_MESSAGE}</p>
        )}
        {roleFit !== null && primary && (
          <section
            className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4"
            data-testid="role-fit-hero"
          >
            <p className="text-xs uppercase tracking-wide text-slate-500">最適職種</p>
            <p className="text-2xl font-semibold text-slate-900">{roleLabels[primary.role]}</p>
            {roleFit.primaryRationale && (
              <p className="text-sm leading-7 text-slate-700">{roleFit.primaryRationale}</p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {ASSESSMENT_SIDES.map(([side, label]) => (
                <div key={side} className="rounded-lg bg-white p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                  {primary[side] ? (
                    <AssessmentScore assessment={primary[side]} />
                  ) : (
                    <p className="text-sm text-muted-foreground">{ASSESSMENT_MISSING_MESSAGE}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        {roleFit !== null && roleFit.entries.length > 0 && (
          <>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              該当職種 ({roleFit.entries.length})
            </p>
            {roleFit.entries.map((entry) => (
              <section
                key={entry.role}
                className="space-y-3 rounded-xl border border-slate-200 p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{roleLabels[entry.role]}</h3>
                  {entry.role === roleFit.primaryRole && <Badge variant="secondary">最適</Badge>}
                </div>
                <p className="text-sm leading-7 text-slate-700">{entry.fitRationale}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  {ASSESSMENT_SIDES.map(([side, label]) => (
                    <AssessmentColumn key={side} label={label} assessment={entry[side]} />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
